import React, { useState } from 'react';
import { Box, Button, Typography, LinearProgress, Card, Container } from '@mui/material';
import ExcelJS from 'exceljs';
import { createUserAccount } from './api';
import { styled } from '@mui/material/styles';

// Styled Components
const ExtravagantContainer = styled(Container)(({ theme }) => ({
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #3a1c71 0%, #d76d77 50%, #ffaf7b 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(4),
  }));
  
  const ExtravagantCard = styled(Card)(({ theme }) => ({
    borderRadius: theme.spacing(2),
    boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
    overflow: 'hidden',
    position: 'relative',
    background: 'rgba(255, 255, 255, 0.85)',
    backdropFilter: 'blur(8px)',
    width: '100%',
    maxWidth: 700,
  }));
  
  const HeaderBox = styled(Box)(({ theme }) => ({
    background: 'linear-gradient(90deg, #ffaf7b, #d76d77)',
    padding: theme.spacing(3),
    textAlign: 'center',
    color: '#fff',
  }));
  
  const StyledButton = styled(Button)(({ theme }) => ({
    marginTop: theme.spacing(3),
    padding: theme.spacing(1.5),
    fontSize: '1rem',
    background: 'linear-gradient(45deg, #d76d77, #ffaf7b)',
    color: '#fff',
    boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
    transition: 'transform 0.2s, box-shadow 0.2s',
    '&:hover': {
      transform: 'scale(1.02)',
      boxShadow: '0 6px 16px rgba(0,0,0,0.3)',
    },
  }));
  
  // Main Component
  const BulkUserImport = () => {
    const [uploading, setUploading] = useState(false);
    const [log, setLog] = useState([]);
  
    const handleFileUpload = async (event) => {
      const file = event.target.files[0];
      if (!file) return;
  
      setUploading(true);
      setLog([]);
  
      const reader = new FileReader();
  
      reader.onload = async (e) => {
        try {
          const buffer = e.target.result;
          const workbook = new ExcelJS.Workbook();
          await workbook.xlsx.load(buffer);
          const worksheet = workbook.worksheets[0];
  
          const users = [];
  
          worksheet.eachRow((row, rowIndex) => {
            if (rowIndex === 1) return;
  
            const fullName = row.getCell(1).value?.toString().trim();
            const email = row.getCell(2).value?.toString().trim();
            const phone = row.getCell(3).value?.toString().trim();
            const amountPaid = row.getCell(4).value;
            const course = row.getCell(5).value?.toString().trim();
  
            if (!fullName || !email || !phone || !amountPaid || !course) return;
  
            const [firstName, ...rest] = fullName.split(' ');
            const lastName = rest.join(' ') || '';
  
            users.push({ firstName, lastName, email, phone, amountPaid, course });
          });
  
          for (const user of users) {
            try {
              const paymentUserData = {
                email: user.email,
                firstName: user.firstName,
                lastName: user.lastName,
                phone: user.phone,
              };
  
              const accountResult = await createUserAccount(paymentUserData, {
                amountPaid: user.amountPaid * 100, // assume major units in sheet
                // An import is not a payment taken today; payments@ should not
                // get one alert per spreadsheet row.
                skipEmail: true,
              });
  
              if (!accountResult.success) {
                setLog((prev) => [...prev, `❌ ${user.email} - ${accountResult.error}`]);
              } else {
                setLog((prev) => [...prev, `✅ ${user.email} imported successfully`]);
              }
            } catch (err) {
              setLog((prev) => [...prev, `❌ ${user.email} - ${err.message}`]);
            }
          }
        } catch (err) {
          setLog([`❌ Failed to parse file: ${err.message}`]);
        } finally {
          setUploading(false);
        }
      };
  
      reader.readAsArrayBuffer(file);
    };
  
    return (
      <ExtravagantContainer maxWidth={''}>
        <ExtravagantCard>
          <HeaderBox>
            <Typography variant="h4">Bulk Import Candidates</Typography>
            <Typography variant="body2">Upload an Excel file to create WordPress accounts and orders</Typography>
          </HeaderBox>
          <Box p={4}>
            <input type="file" accept=".xlsx" onChange={handleFileUpload} />
            {uploading && <LinearProgress sx={{ mt: 2, mb: 2 }} />}
            <Box mt={3}>
              {log.map((entry, index) => (
                <Typography key={index} variant="body2" color={entry.startsWith('✅') ? 'green' : 'error'}>
                  {entry}
                </Typography>
              ))}
            </Box>
          </Box>
        </ExtravagantCard>
      </ExtravagantContainer>
    );
  };
  
  export default BulkUserImport;
  
